// Legal document viewer + re-consent screen.
// Fetches markdown from /api/legal/{privacy-policy,terms-of-service}
// and renders via react-markdown. When TEMPLATE_UNVERIFIED is true we show
// a bright warning banner at the top of every legal page.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { BigButton } from "../design/BigButton";
import { useAuth } from "../contexts/AuthContext";

function TemplateBanner() {
  return (
    <div data-testid="legal-template-banner"
         style={{ background: "#fbeeda", border: "2px solid #d68a1a",
                  borderRadius: 10, padding: "0.75rem 1rem", margin: "1rem 0",
                  color: "#7a4d05", fontSize: "0.85rem", fontWeight: 600 }}>
      ⚠️ Working template pending attorney review. Do not treat as final
      legal text. See LAUNCH_CHECKLIST for owner actions.
    </div>
  );
}

function DocView({ endpoint, title, testid }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(endpoint);
        setDoc(data);
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, [endpoint]);

  if (err) return <div data-testid={`${testid}-error`}>Failed to load: {err}</div>;
  if (!doc) return <div data-testid={`${testid}-loading`}>Loading…</div>;
  return (
    <div data-testid={testid} style={{ maxWidth: 780, margin: "0 auto",
                                        padding: "1.5rem", lineHeight: 1.55 }}>
      <div style={{ fontSize: "0.75rem", color: "#666" }}>
        v{doc.version} · updated {doc.lastUpdated}
      </div>
      {doc.templateUnverified ? <TemplateBanner /> : null}
      <ReactMarkdown>{doc.body}</ReactMarkdown>
      <p style={{ marginTop: "2rem", fontSize: "0.8rem" }}>
        <Link to="/" data-testid={`${testid}-home-link`}>← Back to home</Link>
      </p>
    </div>
  );
}

export function PrivacyPolicyPage() {
  return <DocView endpoint="/legal/privacy-policy"
                  title="Privacy Policy"
                  testid="privacy-policy-page" />;
}

export function TermsOfServicePage() {
  return <DocView endpoint="/legal/terms-of-service"
                  title="Terms of Service"
                  testid="terms-of-service-page" />;
}

export function ReConsentPage() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [current, setCurrent] = useState({ privacy: "", terms: "" });
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/legal/versions");
        setCurrent({ privacy: data.privacyPolicyVersion, terms: data.termsOfServiceVersion });
      } catch (_e) { /* backend unreachable — server will still 422 if we submit wrong versions */ }
    })();
  }, []);

  async function submit() {
    if (!accepted) return;
    setBusy(true); setErr(null);
    try {
      await api.post("/auth/accept-legal", {
        privacyPolicyVersion: current.privacy,
        termsOfServiceVersion: current.terms,
      });
      await refresh();
      navigate("/", { replace: true });
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="re-consent-page" style={{ maxWidth: 780, margin: "0 auto",
                                                 padding: "1.5rem" }}>
      <h1>Please review our updated legal documents</h1>
      <p>We've updated our Privacy Policy or Terms of Service. Please review
         and re-accept to continue.</p>
      <TemplateBanner />
      <p>
        <Link to="/legal/privacy" target="_blank" rel="noopener"
              data-testid="re-consent-privacy-link">Read the Privacy Policy (v{current.privacy})</Link>
        {" · "}
        <Link to="/legal/terms" target="_blank" rel="noopener"
              data-testid="re-consent-terms-link">Read the Terms of Service (v{current.terms})</Link>
      </p>
      <label style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", margin: "1.5rem 0" }}>
        <input type="checkbox" checked={accepted}
               onChange={(e) => setAccepted(e.target.checked)}
               data-testid="re-consent-accept" />
        <span>I have read and agree to the updated Privacy Policy and Terms of Service.</span>
      </label>
      {err ? <div style={{ color: "#c33", marginBottom: "1rem" }}
                  data-testid="re-consent-error">{err}</div> : null}
      <BigButton onClick={submit} disabled={busy || !accepted}
                 data-testid="re-consent-submit">
        {busy ? "Saving…" : "Accept and continue"}
      </BigButton>
    </div>
  );
}
